import { z } from 'zod'
import i18next from 'i18next'
import { errorParentIsRemoved, Mutator } from './base'
import { DataBase } from '@/lib/kysely'
import { useUserStore } from '@/stores/user'
import type { InsertQuery, UpdateQuery, DeleteQuery } from '@/lib/entity/base'

import { graphql } from '@/gql'
import type {
  Coassign_Assign_Plan_Insert_Input,
  Coassign_Assign_Plan_Set_Input
} from '@/gql/graphql'
import type { MutationOperationAndVariables } from '@/lib/mutationHelpers'
import { formatISO } from 'date-fns'
import { pick } from '@/lib/utils/functional'
import { type Reactive } from 'vue'
import { isExist } from '@/lib/relation-graph'

export const assignPlanSchema = z
  .object({
    id: z.string().max(21),
    assign_id: z.string().max(21).refine(isExist('assign'), errorParentIsRemoved),
    start_offset: z
      .number()
      .min(0, i18next.t('stages.labels.error.zod.offsetNegative', { ns: 'translation' })),
    end_offset: z
      .number()
      .min(0, i18next.t('stages.labels.error.zod.offsetNegative', { ns: 'translation' })),
    estimate: z
      .number()
      .min(0, i18next.t('stages.labels.error.zod.offsetNegative', { ns: 'translation' }))
      .transform((v) => Math.round(v * 3600))
  })
  .refine(
    (data) => data.end_offset >= data.start_offset,
    i18next.t('stages.labels.error.zod.endDateAfterStartDate', { ns: 'translation' })
  )

export type AssignPlan = z.infer<typeof assignPlanSchema>

export class AssignPlanMutator extends Mutator<typeof assignPlanSchema> {
  static readonly stageableClassName = 'AssignPlanMutator'
  schema = assignPlanSchema
  table = 'assign_plan' as const

  static async load(
    id: string,
    context: { assign_id: string }
  ): Promise<Reactive<AssignPlanMutator>> {
    const self = AssignPlanMutator.createInstance()
    // FIXME: need use watch query
    const base = await DataBase.selectFrom(self.table)
      .selectAll()
      .where('id', '=', id)
      .executeTakeFirst()

    if (base) self.base = { ...base, estimate: base.estimate / 3600 }
    self.context = { ...context, id }
    return self
  }

  static async listByAssignId(assignId: string) {
    const bases = await DataBase.selectFrom('assign_plan')
      .selectAll()
      .where('assign_id', '=', assignId)
      .execute()

    return bases.map((base) => {
      const self = AssignPlanMutator.createInstance()
      self.base = { ...base, estimate: base.estimate / 3600 }
      self.context = {
        id: base.id
      }
      return self
    })
  }

  async updateLocalDB(): Promise<UpdateQuery[]> {
    const id = this.context.id!
    const rawData = { ...(await this.parsed)! }
    const params = pick(rawData, Object.keys(this.modified) as (keyof typeof this.modified)[])
    return [DataBase.updateTable(this.table).set(params).where('id', '=', id).compile()]
  }

  async insertLocalDB(): Promise<InsertQuery[]> {
    const tenant_id = useUserStore().userInfo!.tenantId
    const params = { ...(await this.parsed)!, ...{ tenant_id } }
    return [DataBase.insertInto(this.table).values(params).compile()]
  }

  async deleteLocalDB(): Promise<DeleteQuery[]> {
    const id = this.context.id!
    return [DataBase.deleteFrom(this.table).where('id', '=', id).compile()]
  }

  async asInsertMutation(commit_id: string): Promise<MutationOperationAndVariables[]> {
    const params = (await this.parsed)!
    const variables: Coassign_Assign_Plan_Insert_Input = { ...params, ...{ commit_id } }
    return [{ mutation: insertAssignPlanMutationDocument, variables: { input: variables } }]
  }

  async asDeleteMutation(commitId: string): Promise<MutationOperationAndVariables[]> {
    const id = this.context.id!
    const variables: Coassign_Assign_Plan_Set_Input = {
      commit_id: commitId,
      deleted_at: formatISO(new Date())
    }
    return [{ mutation: updateAssignPlanMutationDocument, variables: { id: id, input: variables } }]
  }

  async asUpdateMutation(commit_id: string): Promise<MutationOperationAndVariables[]> {
    const id = this.context.id!
    const rawData = { ...(await this.parsed)! }
    const params = pick(rawData, Object.keys(this.modified) as (keyof typeof this.modified)[])
    const variables: Coassign_Assign_Plan_Set_Input = { ...params, ...{ commit_id } }
    return [{ mutation: updateAssignPlanMutationDocument, variables: { id: id, input: variables } }]
  }
}

const insertAssignPlanMutationDocument = graphql(/* GraphQL */ `
  mutation insertAssignPlan($input: coassign_assign_plan_insert_input!) {
    insert_coassign_assign_plan_one(object: $input) {
      created_at
    }
  }
`)

const updateAssignPlanMutationDocument = graphql(/* GraphQL */ `
  mutation updateAssignPlan($id: String!, $input: coassign_assign_plan_set_input!) {
    update_coassign_assign_plan_by_pk(pk_columns: { id: $id }, _set: $input) {
      updated_at
    }
  }
`)
